import React, { useEffect } from 'react';
import { AlertTriangleIcon, InfoIcon, ShieldIcon, ClockIcon, CheckCircleIcon } from 'lucide-react';
type LimitStatus = 'pass' | 'warn' | 'watch';
interface LimitRow {
  label: string;
  value: string;
  status: LimitStatus;
  note?: string;
}
interface ExposureRow {
  name: string;
  type: 'Obligor' | 'Sector';
  pct: number;
  limit: number;
  status: LimitStatus;
}
interface AlertRow {
  time: string;
  level: 'info' | 'warn' | 'breach';
  message: string;
}
interface ActionRow {
  time: string;
  actor: string;
  action: string;
  result: string;
}
const LIMITS: LimitRow[] = [{
  label: 'Obligor concentration < 20%',
  value: '18.2%',
  status: 'warn',
  note: 'Walmart trending higher than target band'
}, {
  label: 'Top 5 concentration < 60%',
  value: '52.8%',
  status: 'watch'
}, {
  label: 'Sector limit < 35%',
  value: '31.0%',
  status: 'warn',
  note: 'Retail exposure building across anchors'
}, {
  label: 'Average tenor < 60 days',
  value: '42.3d',
  status: 'pass'
}];
const EXPOSURE: ExposureRow[] = [{
  name: 'Walmart',
  type: 'Obligor',
  pct: 18.2,
  limit: 20,
  status: 'warn'
}, {
  name: 'Amazon',
  type: 'Obligor',
  pct: 12.4,
  limit: 20,
  status: 'pass'
}, {
  name: 'Target',
  type: 'Obligor',
  pct: 10.2,
  limit: 20,
  status: 'pass'
}, {
  name: 'Top 5 obligors',
  type: 'Obligor',
  pct: 52.8,
  limit: 60,
  status: 'watch'
}, {
  name: 'Retail',
  type: 'Sector',
  pct: 31.0,
  limit: 35,
  status: 'warn'
}, {
  name: 'Logistics',
  type: 'Sector',
  pct: 18.5,
  limit: 35,
  status: 'pass'
}];
const ALERTS: AlertRow[] = [{
  time: '09:42',
  level: 'breach',
  message: 'External rating feed revised Walmart outlook to A2'
}, {
  time: '09:38',
  level: 'warn',
  message: 'Retail sector exposure approaching internal 35% cap'
}, {
  time: '09:31',
  level: 'info',
  message: 'Model flagged elevated disputes on Anchor D (7D window)'
}, {
  time: '09:14',
  level: 'info',
  message: 'Rolling 30D collection rate improved to 98.7%'
}];
const ACTIONS: ActionRow[] = [{
  time: '09:40',
  actor: 'risk@mint.us',
  action: 'Submitted Walmart internal limit override for Q2 vintage',
  result: 'Pending committee decision'
}, {
  time: '09:20',
  actor: 'system',
  action: 'Tightened pricing band for Anchor B new tickets',
  result: 'Applied to pipeline'
}, {
  time: '08:55',
  actor: 'committee',
  action: 'Approved diversification guideline for next vintage',
  result: 'Live and monitored'
}];
export function RiskManagement() {
  useEffect(() => {
    document.title = 'Mint | Risk Management';
  }, []);
  return <div className="min-h-screen bg-gray-50" data-id="element-2199">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between" data-id="element-2200">
        <div data-id="element-2201">
          <h1 className="text-3xl font-semibold text-gray-900" data-id="element-2202">
            Risk Management
          </h1>
          <p className="text-sm text-gray-600 mt-1" data-id="element-2203">
            Covenant Compliance and Concentration Limits
          </p>
        </div>
        <div className="flex items-center text-xs text-gray-500" data-id="element-2204">
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 mr-2" data-id="element-2205" />
          Live monitoring
        </div>
      </div>
      {/* Top strip: high-level risk KPIs (same grammar as Allocation Engine header) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-id="element-2206">
        <KpiTile label="Overall status" value="Compliant" icon={<ShieldIcon size={18} className="text-gray-500" data-id="element-2208" />} data-id="element-2207" />
        <KpiTile label="Active breaches" value="0" icon={<CheckCircleIcon size={18} className="text-gray-500" data-id="element-2210" />} data-id="element-2209" />
        <KpiTile label="Watchlist items" value="3" icon={<AlertTriangleIcon size={18} className="text-gray-500" data-id="element-2212" />} data-id="element-2211" />
        <KpiTile label="Overrides pending" value="1" icon={<ClockIcon size={18} className="text-gray-500" data-id="element-2214" />} data-id="element-2213" />
      </div>
      {/* Main grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8" data-id="element-2215">
        {/* Left column: limits + exposure */}
        <div className="xl:col-span-2 space-y-6" data-id="element-2216">
          <CardPanel title="Covenant compliance" data-id="element-2217">
            <div className="space-y-3 text-sm" data-id="element-2218">
              {LIMITS.map(row => <LimitRowItem key={row.label} row={row} data-id="element-2219" />)}
            </div>
          </CardPanel>
          <CardPanel title="Concentration and exposure" data-id="element-2220">
            <div className="overflow-x-auto" data-id="element-2221">
              <table className="min-w-full text-xs" data-id="element-2222">
                <thead className="bg-gray-50 text-gray-500 uppercase" data-id="element-2223">
                  <tr data-id="element-2224">
                    <th className="px-3 py-2 text-left" data-id="element-2225">Name</th>
                    <th className="px-3 py-2 text-left" data-id="element-2226">Type</th>
                    <th className="px-3 py-2 text-left" data-id="element-2227">Exposure</th>
                    <th className="px-3 py-2 text-left" data-id="element-2228">Limit</th>
                    <th className="px-3 py-2 text-left" data-id="element-2229">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-2230">
                  {EXPOSURE.map(row => <tr key={row.name} className="hover:bg-gray-50" data-id="element-2231">
                      <td className="px-3 py-2 text-gray-900" data-id="element-2232">{row.name}</td>
                      <td className="px-3 py-2 text-gray-600" data-id="element-2233">{row.type}</td>
                      <td className="px-3 py-2" data-id="element-2234">
                        <ExposureBar pct={row.pct} data-id="element-2235" />
                      </td>
                      <td className="px-3 py-2 text-gray-600" data-id="element-2236">
                        {row.limit.toFixed(1)}%
                      </td>
                      <td className="px-3 py-2" data-id="element-2237">
                        <StatusBadge status={row.status} data-id="element-2238" />
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </CardPanel>
        </div>
        {/* Right column: limits, alerts, decisions */}
        <div className="space-y-6" data-id="element-2239">
          <CardPanel title="Portfolio limits and overrides" data-id="element-2240">
            <div className="space-y-3 text-sm" data-id="element-2241">
              <div className="flex items-start gap-2" data-id="element-2242">
                <AlertTriangleIcon size={16} className="text-gray-500 mt-0.5 flex-shrink-0" data-id="element-2243" />
                <div data-id="element-2244">
                  <div className="text-gray-900" data-id="element-2245">
                    Walmart exposure approaching 20% limit
                  </div>
                  <div className="text-xs text-gray-600 mt-1" data-id="element-2246">
                    Current: 18.2% of AUM. Model projects 19.6% at current
                    pacing.
                  </div>
                  <button className="mt-1 text-xs font-medium text-gray-800 hover:text-gray-900" data-id="element-2247">
                    Submit override request
                  </button>
                </div>
              </div>
              <div className="flex items-start gap-2" data-id="element-2248">
                <InfoIcon size={16} className="text-gray-500 mt-0.5 flex-shrink-0" data-id="element-2249" />
                <div data-id="element-2250">
                  <div className="text-gray-900" data-id="element-2251">Diversification guidance</div>
                  <div className="text-xs text-gray-600 mt-1" data-id="element-2252">
                    Shift next vintage toward logistics and healthcare anchors
                    to relieve retail.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2" data-id="element-2253">
                <InfoIcon size={16} className="text-gray-500 mt-0.5 flex-shrink-0" data-id="element-2254" />
                <div data-id="element-2255">
                  <div className="text-gray-900" data-id="element-2256">
                    Next formal review: Thursday 10:00 AM
                  </div>
                  <div className="text-xs text-gray-600 mt-1" data-id="element-2257">
                    Agenda: concentration, tenor drift, sector mix.
                  </div>
                </div>
              </div>
            </div>
          </CardPanel>
          <CardPanel title="Real-time alerts" data-id="element-2258">
            <div className="space-y-2 text-xs max-h-64 overflow-y-auto" data-id="element-2259">
              {ALERTS.map((alert, idx) => <AlertRowItem key={idx} alert={alert} data-id="element-2260" />)}
            </div>
          </CardPanel>
          <CardPanel title="Recent risk decisions" data-id="element-2261">
            <div className="space-y-2 text-xs max-h-64 overflow-y-auto" data-id="element-2262">
              {ACTIONS.map((a, idx) => <div key={idx} className="border-b border-gray-100 pb-2 last:border-b-0 last:pb-0" data-id="element-2263">
                  <div className="flex items-center justify-between mb-1" data-id="element-2264">
                    <span className="text-gray-500" data-id="element-2265">{a.time}</span>
                    <span className="text-gray-700 font-medium" data-id="element-2266">{a.actor}</span>
                  </div>
                  <div className="text-gray-800" data-id="element-2267">{a.action}</div>
                  <div className="text-gray-500 mt-0.5" data-id="element-2268">{a.result}</div>
                </div>)}
            </div>
          </CardPanel>
        </div>
      </div>
    </div>;
}
/* helper components */
function KpiTile({
  label,
  value,
  icon
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) {
  return <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2269">
      <div className="flex items-start justify-between mb-1" data-id="element-2270">
        <div className="text-[11px] text-gray-500 uppercase tracking-wide" data-id="element-2271">
          {label}
        </div>
        {icon && <div data-id="element-2272">{icon}</div>}
      </div>
      <div className="text-xl font-semibold text-gray-900" data-id="element-2273">{value}</div>
    </div>;
}
function CardPanel({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2274">
      <div className="text-xs font-semibold text-gray-700 uppercase mb-3" data-id="element-2275">
        {title}
      </div>
      {children}
    </div>;
}
function LimitRowItem({
  row
}: {
  row: LimitRow;
}) {
  return <div className="flex items-center justify-between border border-gray-100 rounded-md px-3 py-2 bg-gray-50" data-id="element-2276">
      <div className="flex-1" data-id="element-2277">
        <div className="text-xs text-gray-600" data-id="element-2278">{row.label}</div>
        {row.note && <div className="text-[11px] text-gray-500 mt-1" data-id="element-2279">{row.note}</div>}
      </div>
      <div className="flex items-center gap-2" data-id="element-2280">
        <span className="text-sm font-semibold text-gray-900" data-id="element-2281">{row.value}</span>
        <StatusBadge status={row.status} data-id="element-2282" />
      </div>
    </div>;
}
function StatusBadge({
  status
}: {
  status: LimitStatus;
}) {
  const base = 'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium';
  if (status === 'pass') {
    return <span className={`${base} bg-gray-100 text-gray-800`} data-id="element-2283">Pass</span>;
  }
  if (status === 'warn') {
    return <span className={`${base} bg-gray-100 text-gray-800`} data-id="element-2284">Warn</span>;
  }
  return <span className={`${base} bg-gray-900 text-white`} data-id="element-2285">Watch</span>;
}
function ExposureBar({
  pct
}: {
  pct: number;
}) {
  return <div className="flex items-center gap-2" data-id="element-2286">
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-2287">
        <div className="h-full bg-gray-700" style={{
        width: `${Math.min(100, pct)}%`
      }} data-id="element-2288" />
      </div>
      <span className="w-12 text-right text-gray-700" data-id="element-2289">{pct.toFixed(1)}%</span>
    </div>;
}
function AlertRowItem({
  alert
}: {
  alert: AlertRow;
}) {
  const {
    level
  } = alert;
  const icon = level === 'breach' ? <AlertTriangleIcon size={12} className="text-gray-700" data-id="element-2290" /> : level === 'warn' ? <AlertTriangleIcon size={12} className="text-gray-600" data-id="element-2291" /> : <InfoIcon size={12} className="text-gray-500" data-id="element-2292" />;
  return <div className="flex items-start gap-2 border-b border-gray-100 pb-2 last:border-b-0 last:pb-0" data-id="element-2293">
      <span className="text-gray-500 w-10" data-id="element-2294">{alert.time}</span>
      <span data-id="element-2295">{icon}</span>
      <span className="text-gray-800" data-id="element-2296">{alert.message}</span>
    </div>;
}